/**
 * Offboarding survey: 8 questions. Q1 has follow-up text for
 * "products/carriers" and "training/support". Q3 allows up to 3 selections.
 */
export const SURVEY_QUESTIONS = [
  {
    id: 'q1',
    question:
      'What is the primary reason for your decision to leave the agency?',
    selectType: 'one',
    options: [
      { id: 'different_agency', label: 'Moving to a different agency/upline' },
      { id: 'changing_careers', label: 'Changing careers/industry' },
      {
        id: 'different_line',
        label: 'Focusing on a different line of insurance'
      },
      {
        id: 'compensation',
        label: 'Compensation structure elsewhere'
      },
      {
        id: 'personal',
        label: 'Personal reasons (schedule, family, etc.)'
      },
      {
        id: 'not_writing',
        label: 'Not actively writing business at this time'
      },
      {
        id: 'products',
        label: 'Looking for different products/carriers',
        followUp: {
          question: 'Which products and carriers are you looking for?'
        }
      },
      {
        id: 'training',
        label: 'Access to training/support elsewhere',
        followUp: {
          question: 'What additional training or support are you looking for?'
        }
      },
      {
        id: 'compliance',
        label: 'Compliance/administrative preferences'
      }
    ]
  },
  {
    id: 'q2',
    question:
      "How satisfied were you with your experience with O'Neill Marketing overall?",
    selectType: 'one',
    options: [
      { id: 'very_satisfied', label: 'Very satisfied' },
      { id: 'satisfied', label: 'Satisfied' },
      { id: 'neutral', label: 'Neutral' },
      { id: 'unsatisfied', label: 'Unsatisfied' },
      { id: 'prefer_not', label: 'Prefer not to say' }
    ]
  },
  {
    id: 'q3',
    question: 'Which of the following contributed most to your decision?',
    selectType: 'upToThree',
    options: [
      { id: 'training_dev', label: 'Training and development' },
      { id: 'compensation', label: 'Compensation' },
      { id: 'technology', label: 'Technology/tools' },
      { id: 'communication', label: 'Communication' },
      { id: 'carrier_access', label: 'Carrier access or contracting' },
      { id: 'lead_opportunities', label: 'Lead opportunities' },
      { id: 'independence', label: 'Independence/agency structure' },
      { id: 'outside_offer', label: 'Outside offer' },
      { id: 'changing_agency', label: 'Changing Agency' },
      { id: 'no_longer_producing', label: 'No longer producing' }
    ]
  },
  {
    id: 'q4',
    question:
      'Did you feel you had the support you needed while contracted with us?',
    selectType: 'one',
    options: [
      { id: 'consistently', label: 'Yes, consistently' },
      { id: 'most_time', label: 'Yes, most of the time' },
      { id: 'sometimes', label: 'Sometimes' },
      { id: 'not_often', label: 'Not often' }
    ]
  },
  {
    id: 'q5',
    question: "Would you consider returning to O'Neill in the future?",
    selectType: 'one',
    options: [
      { id: 'yes', label: 'Yes' },
      { id: 'possibly', label: 'Possibly' },
      { id: 'no', label: 'No' }
    ]
  },
  {
    id: 'q6',
    question: "Would you recommend O'Neill to another agent?",
    selectType: 'one',
    options: [
      { id: 'yes', label: 'Yes' },
      { id: 'maybe', label: 'Maybe' },
      { id: 'no', label: 'No' }
    ]
  },
  {
    id: 'q7',
    question: 'Is there anything that would have encouraged you to stay?',
    selectType: 'one',
    options: [
      {
        id: 'different_comp',
        label: 'Different compensation or bonus structure'
      },
      { id: 'more_training', label: 'More training/support' },
      { id: 'better_leads', label: 'Better lead opportunities' },
      { id: 'more_carriers', label: 'More carrier options' },
      { id: 'different_structure', label: 'Different agency structure' },
      {
        id: 'nothing',
        label: "Nothing — my decision was not O'Neill related"
      }
    ]
  },
  {
    id: 'q8',
    question:
      "Would you like to stay on with O'Neill Marketing as an independent agent? It's the easiest way to keep your carrier appointments and portal access, and we're happy to continue supporting you as an independent agent.",
    selectType: 'one',
    options: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No, I do not want to remain contracted' }
    ]
  }
];

/**
 * Agency-initiated offboarding survey (admin offboarding an agent from the table).
 * Q6 can be hidden for downline agencies (see canHideForDownline).
 */
export const AGENCY_INITIATED_SURVEY = [
  {
    id: 'q1',
    question: 'What is the primary reason the agent is being offboarded?',
    selectType: 'one',
    options: [
      { id: 'agent_requested', label: 'Agent requested to leave' },
      { id: 'moving_agency', label: 'Agent moving to another agency/upline' },
      { id: 'no_longer_producing', label: 'Agent no longer producing' },
      {
        id: 'compliance_admin',
        label: 'Compliance or administrative issues'
      },
      {
        id: 'unable_to_reach',
        label: 'Unable to reach agent / inactive'
      },
      { id: 'career_change', label: 'Career change' },
      {
        id: 'personal_reasons',
        label: 'Personal reasons (as communicated by agent)'
      },
      { id: 'other', label: 'Other (non-specific)' }
    ]
  },
  {
    id: 'q2',
    question:
      "How would you describe the agent's engagement with your agency before offboarding?",
    selectType: 'one',
    options: [
      { id: 'highly_engaged', label: 'Highly engaged' },
      { id: 'moderately_engaged', label: 'Moderately engaged' },
      { id: 'occasionally_active', label: 'Occasionally active' },
      { id: 'rarely_active', label: 'Rarely active' },
      {
        id: 'inactive_no_production',
        label: 'Inactive / no recent production'
      }
    ]
  },
  {
    id: 'q3',
    question: 'What were the main contributing factors to this offboarding?',
    selectType: 'upToThree',
    options: [
      { id: 'training_support', label: 'Training or support needs' },
      { id: 'compensation', label: 'Compensation concerns' },
      {
        id: 'technology_systems',
        label: 'Technology or system challenges'
      },
      { id: 'communication', label: 'Communication issues' },
      {
        id: 'carrier_contracting',
        label: 'Carrier access / contracting problems'
      },
      { id: 'lead_opportunities', label: 'Lead opportunities' },
      { id: 'compliance', label: 'Compliance issues' },
      {
        id: 'personal_agent_driven',
        label: 'Personal/agent-driven reasons'
      },
      {
        id: 'admin_responsiveness',
        label: 'Administrative or responsiveness issues'
      }
    ]
  },
  {
    id: 'q4',
    question:
      'Would your agency re-contract or re-onboard this agent in the future?',
    selectType: 'one',
    options: [
      { id: 'yes', label: 'Yes' },
      { id: 'maybe', label: 'Maybe / under certain conditions' },
      { id: 'no', label: 'No' }
    ]
  },
  {
    id: 'q5',
    question:
      'Was this offboarding due to performance, behavior, or compliance issues?',
    selectType: 'one',
    options: [
      { id: 'yes_performance', label: 'Yes — performance-related' },
      { id: 'yes_behavior', label: 'Yes — behavior-related' },
      { id: 'yes_compliance', label: 'Yes — compliance-related' },
      { id: 'no', label: 'No' }
    ]
  },
  {
    id: 'q6',
    question:
      "Does this agent have your approval to obtain a release? Please note the agent will need to request the release from their own O'Neill Agent Portal on the My Profile tab via the Offboarding button.",
    selectType: 'one',
    // canHideForDownline: true,
    options: [
      { id: 'yes', label: 'Yes' },
      {
        id: 'no',
        label: 'No',
        followUp: {
          question:
            'Please provide more context on why you are denying this release.'
        }
      }
    ]
  }
];
